import type { Operation } from "fast-json-patch";
import jsonpatch from "fast-json-patch";

const { applyPatch } = jsonpatch;

export interface ApplyPatchesWithoutAliasingOptions {
  /**
   * How the document is copied before the patches are applied.
   *
   * - `false` (default): structured clone. `Date`s stay `Date`s and
   *   `undefined` properties are kept.
   * - `true`: JSON round trip, exactly like fast-json-patch's
   *   `mutateDocument=false`. `Date`s in the document become ISO strings and
   *   `undefined` properties are dropped.
   *
   * Patch values are structured-cloned in both modes, so a `Date` carried by an
   * operation reaches the result as a `Date`.
   */
  normalizeJson?: boolean;
  /**
   * Validate every operation against the document while applying (the
   * `validateOperation` flag of fast-json-patch): an unresolvable path, an
   * out-of-bounds index or an `undefined` value throws a `JsonPatchError`.
   * Default `false`.
   */
  validate?: boolean;
}

/**
 * Applies JSON patches to a document and returns the new document, without
 * mutating the document or the patches and without sharing any object between
 * the patches and the result.
 *
 * fast-json-patch inserts an operation's `value` into the document by
 * reference. Applied to the caller's patches directly, a later operation (for
 * example `add /arr/2` after `replace /arr`) mutates the earlier operation's
 * value, and whoever receives those patches afterwards applies the added item
 * twice. Every operation is therefore cloned on its own, which also keeps two
 * operations that carry the same object from sharing it in the result.
 *
 * The clones are structured clones: values must be plain data (JSON values,
 * `Date`s, `undefined`). Do not pass an observer proxy as `doc`; read its plain
 * value first.
 *
 * Throws what fast-json-patch throws when a patch cannot be applied.
 */
export function applyPatchesWithoutAliasing<T>(
  doc: T,
  patches: readonly Operation[],
  options: ApplyPatchesWithoutAliasingOptions = {},
): T {
  const { normalizeJson = false, validate = false } = options;
  const ownPatches = patches.map((patch) => structuredClone(patch));
  if (normalizeJson) {
    return applyPatch(doc, ownPatches, validate, false).newDocument;
  }
  return applyPatch(structuredClone(doc), ownPatches, validate, true)
    .newDocument;
}
